// Where a notification click goes, and how its `data` reads on the detail
// page (T24). Owner decision: open the linked record when the producer set
// one; otherwise open /notifications/:id with the full message.

import type { CurrencyCode } from '@bbpos/types';
import type { Notification } from '@/services/notification.service';
import { formatCurrency } from '@/utils/currency';

/** React Query keys. The realtime hook invalidates the whole `['notifications']` prefix. */
export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
export const UNREAD_COUNT_QUERY_KEY = ['notifications', 'unread-count'] as const;

/**
 * The role level each linked route needs (its RoleProtectedRoute in
 * router.tsx) — only the routes the producers link to; any other is open to
 * every role. A reader below the gate would be bounced to their start page,
 * so they get the detail view instead: a cashier's own SALE_COMPLETE /
 * APPROVAL_RESULT links to /bills/:id, which is MANAGER+.
 */
const LINK_MIN_ROLE: ReadonlyArray<readonly [string, number]> = [
  ['/bills/', 30],
  ['/products/', 30],
  ['/approvals', 30],
  ['/data-export', 50],
  ['/my-subscription', 50],
  ['/settings/email', 50],
];

/**
 * The record a notification points at, if any, and if the reader's role may
 * open it. Most producers set `action_url`; the email-health and usage alerts
 * send `path` instead. Only in-app routes are accepted — a click must never
 * navigate somewhere arbitrary.
 */
export function notificationLink(
  n: Pick<Notification, 'action_url' | 'metadata'>,
  roleLevel: number = Number.POSITIVE_INFINITY,
): string | null {
  const raw = n.action_url ?? n.metadata?.action_url ?? n.metadata?.path;
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) return null;
  const gate = LINK_MIN_ROLE.find(([prefix]) => raw.startsWith(prefix));
  return gate && roleLevel < gate[1] ? null : raw;
}

/** Where a click goes: the linked record, else the notification's own detail view. */
export function notificationHref(
  n: Pick<Notification, 'id' | 'action_url' | 'metadata'>,
  roleLevel?: number,
): string {
  return notificationLink(n, roleLevel) ?? `/notifications/${n.id}`;
}

// ─── Readable data fields ───────────────────────────────────────────────────

/** Links and ids are not for reading: the link is the "Open record" button. */
const HIDDEN_KEYS = new Set(['action_url', 'path']);
/** Minor-unit amounts the producers send (return approval, sale complete, sales anomaly). */
const MONEY_KEYS = new Set(['amount', 'grand_total', 'weekday_average']);
const PERCENT_KEYS = new Set(['change_pct', 'threshold_pct', 'percent']);
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
/**
 * Keys whose values are codes ('COMPLETED', 'STORE_CREDIT', 'return_approval',
 * 'approved') and read as words. Only these: a name, a number or free text
 * ('MAGGI', 'RINV-1', a comment) is shown as written.
 */
const CODE_KEYS = new Set(['result', 'status', 'scope', 'metric', 'rule']);
const CODE_KEY_SUFFIX = /_(?:type|mode|method|status)$/;

export function humanize(key: string): string {
  const words = key.replace(/_/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface ReadableField {
  key: string;
  label: string;
  value: string;
}

/**
 * The notification's `data` as label / value rows for the detail page. Ids,
 * links, empty values and nested objects are left out; money is shown in the
 * tenant's currency and timestamps on the business's clock.
 */
export function readableDataFields(
  data: Record<string, unknown> | null | undefined,
  opts: { currency: CurrencyCode; timeZone: string },
): ReadableField[] {
  if (!data) return [];
  const rows: ReadableField[] = [];
  for (const [key, raw] of Object.entries(data)) {
    if (HIDDEN_KEYS.has(key) || key === 'id' || key.endsWith('_id')) continue;
    const value = formatValue(key, raw, data, opts);
    if (value !== null) rows.push({ key, label: humanize(key), value });
  }
  return rows;
}

function formatValue(
  key: string,
  raw: unknown,
  data: Record<string, unknown>,
  opts: { currency: CurrencyCode; timeZone: string },
): string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (Array.isArray(raw)) {
    const items = raw.filter((v) => typeof v === 'string' || typeof v === 'number');
    return items.length > 0 ? items.join(', ') : null;
  }
  if (typeof raw === 'object') return null;

  const numeric = typeof raw === 'number' ? raw : /^-?\d+(\.\d+)?$/.test(String(raw)) ? Number(raw) : null;
  if (numeric !== null) {
    if (MONEY_KEYS.has(key)) return formatCurrency(numeric, opts.currency);
    if (PERCENT_KEYS.has(key)) return `${numeric}%`;
    // A discount request carries basis points for a percentage, minor units for a flat amount.
    if (key === 'discount_value') {
      return data.discount_type === 'PERCENTAGE'
        ? `${numeric / 100}%`
        : formatCurrency(numeric, opts.currency);
    }
    return numeric.toLocaleString('en-IN');
  }

  const text = String(raw);
  if (ISO_DATE_TIME.test(text)) {
    const d = new Date(text);
    if (!Number.isNaN(d.getTime())) return formatDateTime(d, opts.timeZone);
  }
  return CODE_KEYS.has(key) || CODE_KEY_SUFFIX.test(key) ? humanize(text) : text;
}

export function formatDateTime(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(d);
}
